/**
 * ArticleCategoryDetail - Redesigned Article Category Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Collapsible sections
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	SwitchBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageSection, EditPageField } from '../../components/container';
import { ArticleCategoryModel, dfArticleCategoryModel, validArticleCategorySchema } from '../../context/models/articles-category/ArticleCategoryModel';
import { slugHelper } from '../../utils/slugHelper';
import { ComboBoxArticleCategory } from '../../components/shared/hoc-article-category/ComboBoxArticleCategory';
import { Layers, Settings, Menu, FolderTree } from 'lucide-react';

type IArticleCategoryDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: ArticleCategoryModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: ArticleCategoryModel) => void;
};

const ArticleCategoryDetail = (props: IArticleCategoryDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<ArticleCategoryModel>(dfArticleCategoryModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<ArticleCategoryModel>({
		resolver: yupResolver(validArticleCategorySchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: ArticleCategoryModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	const watchChangeName = useWatch({ control, name: 'name', defaultValue: '' });

	useEffect(() => {
		const slug = slugHelper.toSlug(watchChangeName);
		setValue('slug', slug);
		setInitialValues((prev) => ({ ...prev, slug }));
	}, [watchChangeName, setValue]);

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof ArticleCategoryModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Basic Info
	const basicInfoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Tên chuyên mục'
				required
				error={errors.name?.message}
				helpText='Tên hiển thị của chuyên mục bài viết'
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên chuyên mục...'
				/>
			</EditPageField>

			<EditPageField
				label='Slug'
				error={errors.slug?.message}
				helpText='Link truy cập: ten-chuyen-muc'
			>
				<TextBox
					name='slug'
					error={errors.slug?.message}
					onValueChanged={(value) => updateField('slug', value)}
					value={initialValues.slug}
					hasValid={true}
				/>
			</EditPageField>

			<EditPageField
				label='Chuyên mục cha'
				error={errors.parentId?.message}
				helpText='Chọn chuyên mục cha (nếu có)'
			>
				<ComboBoxArticleCategory
					name='parentId'
					onValueChanged={(value) => updateField('parentId', value)}
					value={initialValues.parentId}
					error={errors.parentId?.message}
					placeholder='Chọn chuyên mục cha...'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Menu Settings
	const menuSettingsTab = (
		<div className='space-y-6'>
			<EditPageSection
				title='Menu chính'
				icon={<Menu className='h-4 w-4' />}
			>
				<div className='space-y-4'>
					<SwitchBox
						name='isHidden'
						label='Ẩn khỏi menu chính'
						error={errors.isHidden?.message}
						onValueChanged={(value) => updateField('isHidden', value)}
						value={initialValues.isHidden}
						sizeClass='sm'
					/>
					{!initialValues.isHidden && (
						<EditPageField
							label='Vị trí hiển thị'
							error={errors.sortOrder?.message}
						>
							<NumberBox
								name='sortOrder'
								error={errors.sortOrder?.message}
								onValueChanged={(value) => updateField('sortOrder', value)}
								value={initialValues.sortOrder}
							/>
						</EditPageField>
					)}
				</div>
			</EditPageSection>

			<EditPageSection
				title='Menu phụ'
				icon={<Menu className='h-4 w-4' />}
			>
				<div className='space-y-4'>
					<SwitchBox
						name='isHiddenSub'
						label='Ẩn khỏi menu phụ'
						error={errors.isHiddenSub?.message}
						onValueChanged={(value) => updateField('isHiddenSub', value)}
						value={initialValues.isHiddenSub}
						sizeClass='sm'
					/>
					{!initialValues.isHiddenSub && (
						<EditPageField
							label='Vị trí hiển thị'
							error={errors.sortOrderSub?.message}
						>
							<NumberBox
								name='sortOrderSub'
								error={errors.sortOrderSub?.message}
								onValueChanged={(value) => updateField('sortOrderSub', value)}
								value={initialValues.sortOrderSub}
							/>
						</EditPageField>
					)}
				</div>
			</EditPageSection>

			<EditPageSection
				title='Menu chân trang'
				icon={<Menu className='h-4 w-4' />}
			>
				<div className='space-y-4'>
					<SwitchBox
						name='isHiddenFooter'
						label='Ẩn khỏi menu chân trang'
						error={errors.isHiddenFooter?.message}
						onValueChanged={(value) => updateField('isHiddenFooter', value)}
						value={initialValues.isHiddenFooter}
						sizeClass='sm'
					/>
					{!initialValues.isHiddenFooter && (
						<EditPageField
							label='Vị trí hiển thị'
							error={errors.sortOrderFooter?.message}
						>
							<NumberBox
								name='sortOrderFooter'
								error={errors.sortOrderFooter?.message}
								onValueChanged={(value) => updateField('sortOrderFooter', value)}
								value={initialValues.sortOrderFooter}
							/>
						</EditPageField>
					)}
				</div>
			</EditPageSection>
		</div>
	);

	// Tab content: Status
	const statusTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Trạng thái hoạt động'
			>
				<SwitchBox
					name='inActive'
					label='Ngừng áp dụng chuyên mục này'
					error={errors.inActive?.message}
					onValueChanged={(value) => updateField('inActive', value)}
					value={initialValues.inActive}
					sizeClass='sm'
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'basic',
			label: 'Thông tin cơ bản',
			icon: <Layers className='h-4 w-4' />,
			content: basicInfoTab,
		},
		{
			id: 'menu',
			label: 'Cài đặt Menu',
			icon: <Menu className='h-4 w-4' />,
			content: menuSettingsTab,
		},
		{
			id: 'status',
			label: 'Trạng thái',
			icon: <Settings className='h-4 w-4' />,
			content: statusTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới chuyên mục bài viết' : 'Chỉnh sửa chuyên mục bài viết'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Chuyên mục bài viết', href: '/articles-category' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default ArticleCategoryDetail;
